import { applyFeatureFlagOverride, getOverride } from "@Light/override";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createContext, useContext } from "react";
import { Configuration, LoginToken } from "./apiTypes";

let csrfToken: string | null = null;
let flowSessionKey: string | null = null;

export const prepareHeaders = (headers: any) => {
  if (csrfToken) {
    headers.set("X-CSRFToken", csrfToken);
  }

  if (flowSessionKey) {
    headers.set("Flow-Session-Key", flowSessionKey);
  }

  return headers;
};

export const baseQuery = fetchBaseQuery({
  baseUrl: "/api/v1",
  prepareHeaders,
});

export function createBaseQuery(baseQuery: any) {
  return async (args: any, api: any, extraOptions: any) => {
    // Use base query and capture the response
    const result = await baseQuery(args, api, extraOptions);

    // Intercept the session token from any response
    if (result?.meta?.response?.headers) {
      const newSessionToken =
        result.meta.response.headers.get("Flow-Session-Key");
      if (newSessionToken) {
        flowSessionKey = newSessionToken;
      }
      const newCsrfToken = result.meta.response.headers.get("X-CSRFToken");
      if (newCsrfToken) {
        csrfToken = newCsrfToken;
      }
    }

    return result;
  };
}

export const api = createApi({
  reducerPath: "api",
  baseQuery: createBaseQuery(baseQuery),
  tagTypes: ["Configuration"],
  endpoints: (builder) => ({
    getConfiguration: builder.query<Configuration, string | undefined>({
      query: (fingerprint) => ({
        url: "/configuration",
        method: "GET",
        params: fingerprint ? { fingerprint } : undefined,
      }),
      providesTags: ["Configuration"],
      transformResponse: (response: Configuration) => {
        const override = getOverride();
        const feature_flags = applyFeatureFlagOverride(response.feature_flags);
        if (!override) return { ...response, feature_flags };
        return {
          ...response,
          feature_flags,
          company_name: override.companyName ?? response.company_name,
          legal_entity_name: override.companyName ?? response.legal_entity_name,
          support_email: override.supportEmail ?? response.support_email,
          logo: override.logo ?? response.logo,
          favicon_logo: override.icon ?? response.favicon_logo,
          brand_mode: override.brandMode ?? response.brand_mode,
        };
      },
    }),

    login: builder.mutation<void, LoginToken>({
      query: (body) => ({
        url: "/login",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Configuration"],
    }),
    logout: builder.mutation<void, void>({
      query: (body) => ({
        url: "/logout",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Configuration"],
    }),
  }),
});

export const { useGetConfigurationQuery } = api;

export const APIContext = createContext(api);
export function useAPI() {
  return useContext(APIContext);
}
